/**
 * Shared helpers for the two REAL EngineBinding impls — NativeEngineBinding
 * (Tauri commands) and WasmBackedEngineBinding (wasm-pack). Centralizing the
 * view map, engine-JSON shapes, settings merge, output mapping, and the serial
 * batch loop keeps native + wasm behavior identical (the parity property) and DRY.
 */
import {
  backgroundForFormat,
  clampOutputSize,
  enginePolygonToTs as fromEnginePoly,
  wireBackgroundToEngine,
  type EngineRenderSettings,
} from "./adapter.js";
import type { ComposeMockupArgs, EngineBinding, MockupSessionArgs } from "./binding.js";
import { hashHex } from "./hash.js";
import type {
  Detection,
  JobResult,
  Mask,
  MockupAssets,
  Polygon,
  ProgressCallback,
  RenderBatchJob,
  RenderOutput,
  RenderRequest,
  TraceCallback,
  ViewName,
} from "./types.js";
import type { WireTemplateVersion, WireViewType } from "./wire.js";

/** Client view → engine view enum (the engine renders back/front/angle/isometric). */
export const VIEW_TO_ENGINE: Record<string, string> = {
  back: "back",
  front: "front",
  left: "angle",
  right: "angle",
  top: "isometric",
};

/** Engine-side JSON shapes as they cross the Tauri / wasm-bindgen boundary. */
export interface EnginePolyJs {
  points: { x: number; y: number }[];
}
export interface EngineDetectionJs {
  print_area: EnginePolyJs;
  cutouts: { kind: string; polygon: EnginePolyJs }[];
  confidence: number;
  /** Omitted by the engine when empty (`skip_serializing_if`). */
  notes?: string[];
}
export interface EngineRenderOutJs {
  image_bytes: Uint8Array | number[];
  width: number;
  height: number;
  content_hash: string;
}

/** Map an engine Detection (JSON) back to the client Detection shape. */
export function mapEngineDetection(det: EngineDetectionJs): Detection {
  return {
    printArea: fromEnginePoly(det.print_area),
    cutouts: (det.cutouts ?? []).map((c) => ({ kind: c.kind as Mask["kind"], polygon: fromEnginePoly(c.polygon) })),
    confidence: det.confidence,
    ...(det.notes?.length ? { notes: [...det.notes] } : {}),
  };
}

/** Merge per-request output settings onto the template's engine render settings. */
export function buildEngineRenderSettings(engSettings: EngineRenderSettings, req: RenderRequest["settings"]) {
  // ENG-1: the per-request width/height override the (already-clamped) template settings, so
  // they need the same clamp — otherwise a workspace "Output size" of 999999 walks straight
  // past the adapter and into the engine's canvas allocation.
  const [output_width, output_height] = clampOutputSize(
    req.width ?? engSettings.output_width,
    req.height ?? engSettings.output_height,
    [engSettings.output_width, engSettings.output_height],
  );
  return {
    ...engSettings,
    formats: [req.format],
    output_width,
    output_height,
    jpg_quality: req.quality ?? engSettings.jpg_quality,
    // F-1: this is the ONE seam that knows the REQUESTED output format, and the format is per
    // OUTPUT — an export renders the same template to several marketplace presets, three of
    // the four built-in ones being JPG. A transparent canvas has nowhere to live in a JPEG, and
    // the engine's encoder flattens by dropping alpha (→ BLACK), so resolve the background
    // against the container here rather than leaving it to the encoder.
    background: backgroundForFormat(engSettings.background, req.format),
  };
}

/** Build the client RenderOutput from the engine's render result. */
export function toRenderOutput(req: RenderRequest, out: EngineRenderOutJs, engineVersion: string): RenderOutput {
  const bytes = out.image_bytes instanceof Uint8Array ? out.image_bytes : Uint8Array.from(out.image_bytes ?? []);
  return {
    designSlug: req.designSlug,
    view: req.view,
    format: req.settings.format,
    bytes,
    contentHash: out.content_hash,
    width: out.width,
    height: out.height,
    engineVersion,
  };
}

/** Read width/height from encoded PNG/JPEG/WebP bytes; [0,0] when unrecognized. The layered
 *  compositor now encodes the workspace's mockup format, so PNG-only sniffing would zero the
 *  dimensions of every jpg/webp output.
 *  Exported for the ENG-8 tests — dimensions of `[0,0]` on a mockup output are a bug, not a
 *  cosmetic detail: they make the export sheet and the dedup/determinism checks meaningless. */
export function imageSize(b: Uint8Array): [number, number] {
  const u32be = (o: number) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
  // PNG: IHDR width/height at 16/20 (24-byte minimum — the old pngSize contract).
  if (b.length >= 24 && b[0] === 0x89 && b[1] === 0x50) return [u32be(16), u32be(20)];
  if (b.length < 30) return [0, 0]; // JPEG/WebP probes below read up to offset 29
  // JPEG: scan markers for SOF0..SOF15 (except DHT/DAC/RST), height/width big-endian at +5/+7.
  if (b[0] === 0xff && b[1] === 0xd8) {
    let o = 2;
    while (o + 9 < b.length) {
      if (b[o] !== 0xff) { o++; continue; }
      const m = b[o + 1];
      // ENG-8: standalone markers (TEM, RSTn, SOI, EOI) carry NO length field. Treating the
      // next two bytes as a segment length walked the scan off into the entropy-coded data
      // and returned [0,0]. Skip them as 2-byte markers instead. 0xff is a fill byte.
      if (m === 0xff) { o++; continue; }
      if (m === 0x01 || m === 0xd8 || m === 0xd9 || (m >= 0xd0 && m <= 0xd7)) { o += 2; continue; }
      if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
        return [(b[o + 7] << 8) | b[o + 8], (b[o + 5] << 8) | b[o + 6]];
      }
      const len = (b[o + 2] << 8) | b[o + 3];
      if (len < 2) return [0, 0]; // corrupt segment length — bail rather than loop forever
      o += 2 + len;
    }
    return [0, 0];
  }
  // WebP: RIFF….WEBP + VP8X (canvas at 24/27, 24-bit LE, minus one) | VP8L | VP8 (lossy).
  if (b[0] === 0x52 && b[1] === 0x49 && b[8] === 0x57 && b[9] === 0x45) {
    const tag = String.fromCharCode(b[12], b[13], b[14], b[15]);
    if (tag === "VP8X") return [1 + (b[24] | (b[25] << 8) | (b[26] << 16)), 1 + (b[27] | (b[28] << 8) | (b[29] << 16))];
    if (tag === "VP8L") {
      const bits = b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24);
      return [1 + (bits & 0x3fff), 1 + ((bits >> 14) & 0x3fff)];
    }
    if (tag === "VP8 ") return [(b[26] | (b[27] << 8)) & 0x3fff, (b[28] | (b[29] << 8)) & 0x3fff];
  }
  return [0, 0];
}

/** Wire `view_type` → the engine's four view buckets, mirroring `VIEW_TO_ENGINE` for the
 *  wire-side spelling (`isometric` rather than the client's `top`). */
const WIRE_VIEW_TO_ENGINE: Record<WireViewType, string> = {
  back: "back",
  front: "front",
  left: "angle",
  right: "angle",
  isometric: "isometric",
};

/**
 * ENG-3: resolve the brand-logo placement quad for `view` from the template version the job
 * already carries.
 *
 * The brand logo was silently DISCARDED on every layered mockup. `composeMockup` needs BOTH
 * a logo and a quad — the engine skips brand compositing when either is missing — and while
 * the logo arrived via `job.brandLogo`, the quad was only ever read from
 * `MockupAssets.brand`, which no caller has ever populated (`mockupStore` explicitly persists
 * "MockupAssets minus brand/background"). So `brandQuad` was unconditionally null and the
 * feature was a no-op wherever the layered path ran, i.e. every real export.
 *
 * No new plumbing is needed: `job.templateVersion` is the canonical wire version and each
 * `WireView` already carries `brand_logo_placement` — the same field the CLASSIC path reads
 * through the adapter (`wireViewToEngine`). We just read it on this path too.
 */
export function brandQuadForView(
  templateVersion: WireTemplateVersion | undefined | null,
  view: ViewName,
): Polygon | null {
  const views = templateVersion?.payload?.views ?? [];
  if (views.length === 0) return null;
  // Exact wire spelling first, then the engine-bucket equivalence so a client "top" finds an
  // "isometric" view (and "left"/"right" both find an "angle" view), exactly as the classic
  // path's VIEW_TO_ENGINE mapping does.
  const wanted = VIEW_TO_ENGINE[view] ?? view;
  const match =
    views.find((v) => v.view_type === view) ??
    views.find((v) => (WIRE_VIEW_TO_ENGINE[v.view_type] ?? v.view_type) === wanted);
  const quad = match?.brand_logo_placement;
  if (!quad || quad.length < 4) return null;
  // Reject a non-finite quad here rather than handing NaN to the engine — the Rust side now
  // errors on it (ENG-4), and failing the whole item over a bad OPTIONAL logo placement would
  // be worse than just skipping the logo.
  const points = quad.slice(0, 4).map(([x, y]) => ({ x, y }));
  if (points.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;
  return points;
}

/**
 * Render one item as a LAYERED MOCKUP (spec 019): build the fill stack (phone for a
 * clear phone case → design → texture) and composite it over the baked shell/ground
 * via `composeMockup`. Returns the RenderOutput (PNG), or null to fall back to renderOne.
 */
async function renderMockupItem(
  engine: Pick<
    EngineBinding,
    "composeMockup" | "composeInSession" | "engineVersion" | "backendId"
  >,
  job: RenderBatchJob,
  m: MockupAssets,
  design: Uint8Array,
  designSlug: string,
  view: ViewName,
  onTrace?: TraceCallback,
): Promise<RenderOutput | null> {
  if (!engine.composeMockup) return null;
  // WS-H2 (AUD-3 LB-1): when `runBatch` opened a render session for this view, the item carries
  // ONLY its design bytes — the ~13-17 MB of baked assets crossed once, per view. A missing or
  // expired handle is a CACHE MISS, never a failure: the full per-item call below still runs.
  const sessionId = job.sessionByView?.[view];
  let png: Uint8Array | null = null;
  if (sessionId && engine.composeInSession) {
    try {
      png = await engine.composeInSession(sessionId, design);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!msg.includes(MOCKUP_SESSION_GONE)) throw err;
      // The native cache evicted this handle (or the app restarted mid-run). Correctness never
      // depends on the cache: drop it and pay the full per-item call for the rest of the run.
      if (job.sessionByView) delete job.sessionByView[view];
      onTrace?.({
        itemKey: `${designSlug}:${view}`,
        phase: "render",
        startedAt: Date.now(),
        level: "warn",
        message: `render session for view "${view}" expired — falling back to per-item compose`,
      });
    }
  }
  if (!png) png = await engine.composeMockup(composeArgsFor(job, m, design, view));
  if (!png) return null;
  const [width, height] = imageSize(png);
  return {
    designSlug,
    view,
    format: job.settings.format,
    bytes: png,
    // ENG-8: this was the empty string, so every layered-mockup output was un-dedupable and
    // no determinism/parity check could run on the path that produces virtually all real
    // exports. The classic path's hash is FNV-1a over the engine's raw RGBA; the compositor
    // only ever hands back ENCODED bytes across the boundary, so we hash those instead —
    // salted with the engine version + format so the identity means the same thing (same
    // engine + same inputs ⇒ same hash; different engine build ⇒ different hash). It is not
    // comparable to a classic-path hash, and it isn't meant to be: both are per-path
    // identities for dedup and drift detection.
    //
    // WS-I / D-12 (AUD-3 IM-1): the salt also carries the BACKEND. The GPU and CPU warps differ
    // by up to 1 LSB on the non-varnish path, this hash is over the ENCODED PNG, and one flipped
    // LSB changes the PNG — so two customers on the same engine build, template and design got
    // different hashes purely because one of them owns a GPU. "Same inputs ⇒ same bytes" was
    // false across a fleet. Rather than pay for byte-identity everywhere, D-12 SCOPES the
    // promise to "same engine + same backend ⇒ same hash" and says so in the hash itself.
    // Absent `backendId` (wasm, older desktop) ⇒ "cpu", which is what the wasm build is.
    // NB: the Rust-side `render::export` `content_hash` deliberately gains NO backend salt — it
    // is the cross-target PIXEL oracle that `render-vectors.json` pins and `render-parity.test.ts`
    // asserts from the wasm build, and salting it would make native-with-GPU and wasm differ by
    // definition, deleting the only mechanism that catches a real native↔wasm divergence.
    //
    // INTEGRATION NOTE (WS-H2 × WS-I): this is the ONE tail. WS-H2's restructure funnels both
    // producers into it — the session call above assigns `png`, and the per-item fallback
    // (`if (!png) png = await engine.composeMockup(...)`) assigns the SAME `png` — so the
    // session path and the per-item path salt identically BY CONSTRUCTION. A future re-split of
    // this function must not reintroduce a second hash site; `engine-shared.test.ts`'s two
    // "salts identically on the session path and the per-item path" cases are the pin.
    contentHash: hashHex(png, engine.engineVersion, job.settings.format, engine.backendId ?? "cpu"),
    width,
    height,
    engineVersion: engine.engineVersion,
  };
}

/** Substring of the native sentinel (`lib.rs::MOCKUP_SESSION_GONE`). A dead handle is a CACHE
 *  miss, never a render failure: the item re-composes with full inputs. */
export const MOCKUP_SESSION_GONE = "mockup session not found";

/** The exact `refused` tag the native open answers with when the session budget is full
 *  (`lib.rs::MOCKUP_SESSION_BUDGET`, review-1 I-5(b)). A CONTRACT, not a message: change it in
 *  both places or not at all. */
export const MOCKUP_SESSION_BUDGET = "MOCKUP_SESSION_BUDGET";

/** The static half of `composeArgsFor` — everything except the DESIGN layer. `fills` is fill
 *  slots 1..n (texture etc.); slot 0 is the design and is supplied per item. `fillOpacity` and
 *  `fillBlend` stay FULL arrays (slot 0 = the design) so the engine's per-layer indices never
 *  shift. Built FROM `composeArgsFor` on purpose: one mapping, so the session path cannot
 *  drift from the per-item path (the `compose-forwarding` tripwire asserts both). No byte is
 *  copied — `slice()` copies the fills ARRAY (references), never the buffers. */
const EMPTY_DESIGN = new Uint8Array(0);
export function mockupSessionArgs(
  job: RenderBatchJob,
  m: MockupAssets,
  view: ViewName,
): MockupSessionArgs {
  const a = composeArgsFor(job, m, EMPTY_DESIGN, view);
  return { ...a, fills: a.fills.slice(1) };
}

/**
 * Build the `composeMockup` arguments for one item: the fill stack (phone for a clear phone
 * case → design → texture), the behind layer, the brand placement and the output size.
 *
 * Extracted from `renderMockupItem` (WS-H2) so `mockupSessionArgs` can build the SAME object
 * minus the design — one mapping for both the per-item and the session path.
 */
/**
 * WS-BG — the canvas base colour for the LAYERED (baked-asset) compositor, read from the
 * template's own render settings and the format this output is being encoded to.
 *
 * Deliberately the SAME two decisions the classic render path makes, routed through the SAME two
 * functions rather than re-implemented, so the two surfaces cannot answer differently for one
 * template: a mock-up and a mask render of the same template paint the same background.
 *
 *  1. `wireBackgroundToEngine` — the choice (+ its stored colour) → an sRGB canvas.
 *  2. `backgroundForFormat` — F-1: JPEG has NO ALPHA, and the engine's encoder flattens by
 *     DROPPING the channel, so a transparent canvas encodes as BLACK. Measured in a browser
 *     against the real wasm worker: without this line a `transparent` template exported as a JPG
 *     mock-up came out `[0,0,0,255]` — a black square — where it used to come out white. This is
 *     the format-aware half of the rule, and it belongs here for the same reason it belongs in
 *     `buildEngineRenderSettings`: the format is per OUTPUT (one export can render the same
 *     template to a PNG preset and a JPG preset), so only the caller that knows the requested
 *     format can apply it.
 *
 * `null` (the legacy white canvas) is returned for a template that chose nothing — and for one
 * whose choice resolves to white — so a mock-up that exists today is byte-identical.
 *
 * F2: this takes the choice and colour LOOSE rather than only off a stored payload, because the
 * editor's live mock-up preview has to ask the same question of the picker the operator is
 * touching right now — before any Save exists to read back. One rule, one implementation:
 * `mockupBackgroundColor` below is the stored-payload wrapper.
 */
export function composeBackgroundColor(
  choice: string | null | undefined,
  storedColor: readonly number[] | null | undefined,
  format: string | null | undefined,
): [number, number, number, number] | null {
  if (!choice) return null;
  const rgba = backgroundForFormat(wireBackgroundToEngine(choice, storedColor), format);
  // White IS the compositor's own default: sending it explicitly would be a second way to spell
  // the same canvas, and the "unchanged for existing templates" guarantee is easier to hold when
  // the default path sends nothing at all.
  const isWhite = rgba[0] === 255 && rgba[1] === 255 && rgba[2] === 255 && rgba[3] === 255;
  return isWhite ? null : rgba;
}

/** The same answer for a STORED template version — what the batch/export path renders. */
export function mockupBackgroundColor(
  v: WireTemplateVersion | null | undefined,
  format: string | null | undefined,
): [number, number, number, number] | null {
  const s = v?.payload?.render_settings;
  return composeBackgroundColor(s?.background, s?.background_color, format);
}

export function composeArgsFor(
  job: RenderBatchJob,
  m: MockupAssets,
  design: Uint8Array,
  view: ViewName,
): ComposeMockupArgs {
  const fills: Uint8Array[] = [];
  const fillOpacity: number[] = [];
  const fillBlend: string[] = [];
  fills.push(design); fillOpacity.push(m.opacity?.design ?? 1); fillBlend.push("normal");
  if (m.texture) { fills.push(m.texture); fillOpacity.push(m.opacity?.texture ?? 1); fillBlend.push(m.blendMode?.texture ?? "normal"); }
  // A clear-case phone fills the WHOLE silhouette via the `behind` layer (shows through the
  // clear shell), not the print-area fill stack.
  const behind = m.clearPhone && m.phone ? m.phone : null;
  // ENG-3: a per-view placement baked into the assets wins; otherwise derive it from the
  // template version's `brand_logo_placement` (see `brandQuadForView`). Without this the
  // quad is always null and the engine skips the logo entirely.
  const brandLogo = m.brand?.logo ?? job.brandLogo ?? null;
  const brandQuad = m.brand?.quad ?? (brandLogo ? brandQuadForView(job.templateVersion, view) : null);
  // ENG-1: `0`/undefined means "native baked-asset size" to the compositor, so only clamp when
  // a real size was requested — clamping `undefined` would silently force a resize.
  const wantsSize = typeof job.settings.width === "number" && typeof job.settings.height === "number";
  const [outputWidth, outputHeight] = wantsSize
    ? clampOutputSize(job.settings.width, job.settings.height)
    : [job.settings.width, job.settings.height];
  return {
    generated: m.generated,
    shell: m.shell,
    productMask: m.productMask,
    printMask: m.printMask ?? null,
    printQuad: m.printQuad,
    fills,
    fillOpacity,
    fillBlend,
    // The BAKED-asset background IMAGE (a raster the bake produced), which is a different thing
    // from the template's Background CHOICE — that one rides on `backgroundColor` below.
    background: m.background ?? null,
    // WS-BG — the owner's ask, on the layered path: "if I'm selecting any solid or brand colour,
    // it should come in the background — on the mock-up too". This was the KNOWN gap F-2 named:
    // the compositor started from an opaque white canvas and multiplied the generated ground
    // shadow over it, so the choice could not be honoured without a new input. That input now
    // exists, end to end — `ComposeMockupArgs.backgroundColor` → the wasm glue (`wasm.rs`) → the
    // native command frame → `MockupInputs.background_color` → the cached `StaticStage`
    // (`mockup.rs::prepare_static`, so a session-backed batch pays for it once). The shadow
    // multiplies OVER the colour, which is what "multiply" means; a TRANSPARENT choice starts
    // from an empty canvas, where the multiply is a no-op because every channel of a zero pixel
    // stays zero.
    backgroundColor: mockupBackgroundColor(job.templateVersion, job.settings.format),
    brandLogo,
    brandQuad,
    behind,
    designRotation: m.designRotation ?? 0,
    fillFit: m.fillFit ?? "height",
    designAspect: m.designAspect ?? null,
    // ENG/P0-3: forward the spot-UV calibration. It survives every storage leg (bakedSync
    // hydrates `MockupAssets.varnish`, mockupStore persists it) and the EDITOR PREVIEW passes
    // it to composeMockup — but this batch/export path never did, so the exported file silently
    // used the engine's VarnishParams DEFAULTS and could not match the preview on ANY device,
    // including the one the operator calibrated on. Passed unconditionally: both engines ignore
    // `varnish` unless a fill layer's blend is "uvvarnish", and an absent value still
    // deserializes to `VarnishParams::default()` (intensity 0 ⇒ off) exactly as before.
    varnish: m.varnish,
    printShadingStrength: m.printShadingStrength ?? 0,
    shading: m.shading,
    // Encode the workspace's requested mockup format with REAL bytes of that format —
    // outputs used to be PNG regardless, which the export then (correctly) named .png
    // even when the marketplace wanted webp/jpg.
    outputFormat: job.settings.format,
    jpgQuality: job.settings.quality,
    outputWidth,
    outputHeight,
  };
}

/**
 * WS-H1 (AUD-3 LB-5): a per-item render is RETRIED. The download path has had 3 attempts
 * with backoff since `bakedSync.ts:268-307`; rendering had none, so one transient GPU/driver
 * hiccup or one failed multi-MB IPC allocation on item 380 of 500 left a permanent hole in
 * the export whose only recovery was re-running the whole batch.
 *
 * This retries the SAME per-item call with the SAME frame — it does not touch the item
 * boundary, the job shape, or the IPC framing (WS-H2 owns those).
 */
export const ITEM_ATTEMPTS = 3;
export const ITEM_RETRY_BACKOFF_MS = [400, 1200];

/**
 * Only TRANSIENT causes retry. A degenerate quad, NaN coordinates, a dimension/mask
 * mismatch, a size-cap rejection or a not-Active template are ANSWERS about the inputs
 * (`mockup.rs:886-1015`, `render/mod.rs:43-85`) — re-running them burns GPU minutes and
 * delays the truth. Mirrors `isRetryableStatus` in `templates/bakedSync.ts:271-275`.
 */
export function isRetryableRenderError(message: string): boolean {
  // `NaN` is matched CASE-SENSITIVELY and on a word boundary (review-1 L-2): under the `i`
  // flag it also matched "te-NAN-t" / "mai-NAN-ce"/"ba-NAN-a", so a transient tenant-scoped
  // quota or maintenance error was classified deterministic and never retried. The engine
  // spells its own rejection `NaN` (`mockup.rs`), so the narrow test loses nothing.
  if (/\bNaN\b/.test(message)) return false;
  // The rest stay case-insensitive and deliberately broad: `invalid`/`unsupported` cover a
  // family of input answers whose wording differs across the Rust call sites.
  return !/degenerate|mismatch|not locked|not\s+Active|exceeds|too large|unsupported|invalid|deny_unknown|unknown field/i
    .test(message);
}

/**
 * Serial design×view batch loop with progress + trace callbacks. Both real
 * bindings delegate here so their batch semantics are identical; `engine` is the
 * binding itself (its `renderOne` does the per-item work, native or wasm). When
 * `job.mockup` is present it routes to the layered compositor (`composeMockup`).
 */
export async function runBatchSerially(
  engine: Pick<
    EngineBinding,
    "renderOne" | "composeMockup" | "composeInSession" | "engineVersion" | "backendId"
  >,
  job: RenderBatchJob,
  onProgress?: ProgressCallback,
  onTrace?: TraceCallback,
): Promise<JobResult> {
  const outputs: RenderOutput[] = [];
  const failures: JobResult["failures"] = [];
  const total = job.designs.length * job.views.length;
  let completed = 0;
  for (const d of job.designs) {
    for (const view of job.views) {
      const itemKey = `${d.designSlug}:${view}`;
      const startedAt = Date.now();
      onTrace?.({ itemKey, phase: "render", startedAt, level: "info", message: "render start" });
      // WS-H1 (LB-5): attempt loop around the SAME per-item call. `lastError` empty after the
      // loop means the item produced its one output; non-empty means every attempt is spent.
      let lastError = "";
      let attempts = 0;
      for (let attempt = 1; attempt <= ITEM_ATTEMPTS; attempt++) {
        attempts = attempt;
        try {
          // Spec 019 + Task B3: resolve THIS view's baked assets — a per-view entry
          // (multi-view template) wins over the single job-wide `mockup`. When present,
          // render a LAYERED MOCKUP via the engine compositor (design over the baked
          // shell/ground); else the classic warp-over-background render_one path.
          const viewMockup = job.mockupByView?.[view] ?? job.mockup ?? null;
          // H1 visibility: a missing/zero shading strength silently renders FLAT (the `?? 0`
          // default in renderMockupItem) — surface the effective value so "all flat" batch
          // output is diagnosable from the trace instead of a silent calibration loss.
          // Gated on the first attempt so a retry does not double-log it.
          if (viewMockup && attempt === 1) {
            const eff = viewMockup.shading?.strength ?? viewMockup.printShadingStrength ?? 0;
            onTrace?.({
              itemKey, phase: "render", startedAt, level: eff > 0 ? "info" : "warn",
              message: `mockup compose: shading strength=${eff}${eff > 0 ? "" : " (FLAT — no artwork shading in baked assets)"}`,
            });
          }
          const mockupOut = viewMockup && engine.composeMockup
            ? await renderMockupItem(engine, job, viewMockup, d.design, d.designSlug, view, onTrace)
            : null;
          outputs.push(
            mockupOut ??
              (await engine.renderOne({
                templateVersion: job.templateVersion,
                design: d.design,
                designSlug: d.designSlug,
                view,
                settings: job.settings,
                brandLogo: job.brandLogo,
                // The CLASSIC arm — this is the branch `viewMockup == null` selects, i.e. a
                // template with masks and no bake. `baseImage` is what stops it exporting the
                // artwork on a bare canvas; absent, it renders exactly as it always did.
                baseImage: job.baseImageByView?.[view] ?? undefined,
              })),
          );
          lastError = "";
          onTrace?.({ itemKey, phase: "render", startedAt, endedAt: Date.now(), level: "info", message: "render done" });
          break;
        } catch (err) {
          lastError = err instanceof Error ? err.message : String(err);
          const willRetry = attempt < ITEM_ATTEMPTS && isRetryableRenderError(lastError);
          if (!willRetry) break;
          const delay = ITEM_RETRY_BACKOFF_MS[attempt - 1] ?? 1200;
          onTrace?.({
            itemKey, phase: "render", startedAt, level: "warn",
            message: `render attempt ${attempt}/${ITEM_ATTEMPTS} failed (${lastError}) — retrying in ${delay}ms`,
          });
          await new Promise((r) => setTimeout(r, delay));
        }
      }
      if (lastError) {
        const error = `${lastError} (after ${attempts} attempt${attempts === 1 ? "" : "s"})`;
        failures.push({ designSlug: d.designSlug, view, error });
        onTrace?.({ itemKey, phase: "render", startedAt, endedAt: Date.now(), level: "error", message: error });
      }
      completed++;
      onProgress?.({ completed, total, current: { designSlug: d.designSlug, view } });
    }
  }
  return { outputs, failures };
}
